/**
 * Where the book stands, for the man holding the money.
 *
 * The board answers "what do I win". This answers the other question: what does
 * it cost me if this lands, and is there an outcome that ruins the week.
 *
 * The two numbers are deliberately separated because they answer different
 * questions. WORST CASE is the most that can leave the book on any single set
 * of results — the field's worst outcome, plus any independent props, which can
 * all land. IF THIS LANDS breaks that down name by name, because exactly one
 * man wins the outright and every other backer's stake stays where it is.
 *
 * Worst case used to add every open market's liability together, which on a
 * board of thirteen outrights was a total with no outcome behind it: they
 * cannot all win the same tournament. A bookie reading it was being frightened
 * by arithmetic rather than by his own position.
 */

import { useState } from 'react'
import {
  bookSummary,
  exclusiveOutcomes,
  marketExposure,
  type BookBet,
  type BookMarket,
} from '../engine/book'
import { formatMoney } from '../engine/odds'
import type { Tournament } from '../engine/types'
import { playerName } from '../select'
import type { Book } from '../store'
import { SectionRule } from '../ui'

/** Signed money, so a loss reads as a loss rather than as a smaller win. */
const signed = (cents: number) =>
  `${cents < 0 ? '−' : cents > 0 ? '+' : ''}${formatMoney(Math.abs(cents))}`

function Stat({ k, v, tone }: { k: string; v: string; tone?: 'bad' | 'good' }) {
  return (
    <div className="px-4 py-2 flex items-baseline justify-between border-b border-rule">
      <span className="bc-label text-mute">{k}</span>
      <span
        className={`bc-num text-base ${
          tone === 'bad' ? 'text-signal' : tone === 'good' ? '' : ''
        }`}
      >
        {v}
      </span>
    </div>
  )
}

export function BookPosition({
  doc,
  book,
  isAdmin,
}: {
  doc: Tournament
  book: Book
  /** Only changes the framing — the numbers are the same for everyone. */
  isAdmin: boolean
}) {
  const [open, setOpen] = useState<string | null>(null)

  const markets = book.odds as unknown as BookMarket[]
  const bets = book.bets as unknown as BookBet[]

  const s = bookSummary(markets, bets)
  // Outrights are the mutually exclusive family: one man wins the thing.
  const outrights = markets.filter((m) => m.market === 'outright')
  const grid = exclusiveOutcomes(outrights, bets)
  const worst = grid[0]
  /*
   * What the props alone could cost, if every one of them landed.
   *
   * They are independent questions — several can come in together — so they
   * are summed rather than treated as alternatives, which is exactly how
   * `bookSummary` folds them into the worst case. This is that same number,
   * shown, so the total below stops looking like it came from nowhere.
   */
  const props = markets.filter(
    (m) => !(m.market === 'outright' && m.subject_id) && m.status !== 'settled',
  )
  const propLiabilityCents = props.reduce(
    (n, m) => n + marketExposure(m, bets).liabilityCents,
    0,
  )

  if (markets.length === 0) return null

  return (
    <>
      <SectionRule>{isAdmin ? 'The book' : 'Where the book stands'}</SectionRule>

      {/*
        Accepted first, because it is the one that answers "is anyone betting?".
        Taken is the money that has actually arrived, and on a board where every
        slip is still waiting on Venmo it reads zero — true, and a dead-looking
        screen in the middle of a busy evening.
      */}
      <Stat k="Accepted" v={formatMoney(s.acceptedCents)} />
      <Stat k="Taken (confirmed)" v={formatMoney(s.handleCents)} />
      <Stat
        k="Awaiting Venmo"
        v={formatMoney(s.pendingCents)}
        tone={s.pendingCents > 0 ? 'bad' : undefined}
      />
      {/*
        Props, called out separately — they were always inside the worst case
        (they can all land, so they are summed on top of the field's single
        worst outcome), but the outcome grid below only lists outrights, so a
        bookie reading the screen had no way to see where the number came
        from. Shown only once somebody actually has money on one.
      */}
      {propLiabilityCents > 0 && (
        <Stat k="Props, if all land" v={formatMoney(propLiabilityCents)} tone="bad" />
      )}
      <Stat
        k="Worst case"
        v={formatMoney(s.worstCaseCents)}
        tone={s.worstCaseCents > s.handleCents ? 'bad' : undefined}
      />
      {s.settledCents !== 0 && (
        <Stat
          k="Settled so far"
          v={signed(s.settledCents)}
          tone={s.settledCents < 0 ? 'bad' : 'good'}
        />
      )}
      {/*
        The two halves behind that net. "Up $2,075" reads the same whether
        the house has been winning or simply has not paid anybody yet, and
        those are different books — so the kept and the paid are stated,
        with how many rulings each came from.
      */}
      {s.settledKeptCents > 0 && (
        <Stat
          k={`Kept, ${s.settledForHouse} ${s.settledForHouse === 1 ? 'bet' : 'bets'} lost`}
          v={formatMoney(s.settledKeptCents)}
          tone="good"
        />
      )}
      {s.settledPaidCents > 0 && (
        <Stat
          k={`Paid out, ${s.settledForPlayers} ${s.settledForPlayers === 1 ? 'bet' : 'bets'} won`}
          v={formatMoney(s.settledPaidCents)}
          tone="bad"
        />
      )}

      <p className="px-4 py-2 text-[13px] text-mute leading-snug">
        Only confirmed money counts — a slip awaiting Venmo is an intention, not a
        stake. At-risk is what leaves the book on top of the stakes it keeps, since a
        winner&rsquo;s own money was never the book&rsquo;s.
        {propLiabilityCents > 0 && (
          <>
            {' '}
            Props are separate questions and several can land together, so they are
            <b> added on top</b> of the field&rsquo;s single worst outcome rather than
            being one of its alternatives.
          </>
        )}
        {worst && worst.houseNetCents < 0 && (
          <>
            {' '}
            The outcome that hurts most is{' '}
            <b className="bc-name">
              {playerName(doc, worst.market.subject_id ?? '') || worst.market.description}
            </b>{' '}
            at {signed(worst.houseNetCents)}.
          </>
        )}
      </p>

      {grid.length > 0 && (
        <>
          <SectionRule>If this lands</SectionRule>
          <p className="px-4 pt-1 pb-2 text-[13px] text-mute leading-snug">
            One man wins it, so these are alternatives rather than a total: each row is
            the whole book&rsquo;s net if that name comes in and the rest do not. Tap
            for the slips behind it — who is on him, for how much, and at what price.
          </p>
          <ul className="divide-y divide-rule border-t border-rule">
            {grid.map(({ market, exposure, houseNetCents }) => {
              const name =
                playerName(doc, market.subject_id ?? '') || market.description
              const isOpen = open === market.id
              const backers = bets.filter(
                (b) => b.odds_id === market.id && b.status !== 'pending' && b.status !== 'void',
              )
              return (
                <li key={market.id}>
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? null : market.id)}
                    className="w-full text-left px-4 py-2.5 grid grid-cols-[minmax(0,1fr)_auto] gap-3 items-center bc-tap"
                  >
                    <span className="min-w-0">
                      <span className="bc-name text-sm block truncate">{name}</span>
                      <span className="text-[12px] text-mute">
                        {exposure.backers === 0
                          ? 'nobody on it'
                          : `${exposure.backers} on it · ${formatMoney(exposure.handleCents)} taken`}
                      </span>
                    </span>
                    <span
                      className={`bc-num text-base ${houseNetCents < 0 ? 'text-signal' : ''}`}
                    >
                      {signed(houseNetCents)}
                    </span>
                  </button>

                  {isOpen && (
                    <div className="px-4 pb-3">
                      {backers.length === 0 ? (
                        <p className="text-[13px] text-mute">
                          No money on it, so this outcome is a clean sweep of everything
                          else.
                        </p>
                      ) : (
                        <>
                          <ul className="text-[13px]">
                            {backers.map((b, i) => (
                              <li
                                key={i}
                                className="flex justify-between py-0.5 text-mute"
                              >
                                <span className="bc-name text-ink">
                                  {playerName(doc, b.player_id)}
                                </span>
                                <span className="tabular-nums">
                                  {formatMoney(b.stake_cents)} at{' '}
                                  {b.odds_at_placement > 0 ? '+' : ''}
                                  {b.odds_at_placement} → pays{' '}
                                  {formatMoney(
                                    marketExposure(
                                      { ...market, id: market.id },
                                      [b],
                                    ).payoutIfWinCents,
                                  )}
                                </span>
                              </li>
                            ))}
                          </ul>
                          <p className="text-[13px] text-mute mt-1.5 leading-snug">
                            Pays out {formatMoney(exposure.payoutIfWinCents)}, of which{' '}
                            {formatMoney(exposure.liabilityCents)} comes out of the book.
                            Every other name&rsquo;s stake stays put.
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        </>
      )}
    </>
  )
}
